import { animated, config, useSpring } from "@react-spring/web";
import { Box, IconButton } from "@sidelinesports/fan-design-system";
import { ArrowLeftIcon } from "@sidelinesports/icons";
import { clsx } from "clsx";
import React, {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  createContext,
  type ElementType,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Portal } from "../Dialog/Portal";
import { useSlideoutFocus, useSlideoutKeyboardControls } from "./hooks";
import styles from "./SlideoutPanel.module.css";

// Types
interface SlideoutPanelContextValue {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

interface SlideoutPanelRootProps {
  children: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface SlideoutPanelContentProps {
  children: ReactNode;
  /** Accessible name for the panel's `role="dialog"` (announced to screen readers). */
  name: string;
  className?: string;
  onFinishClose?: () => void;
}

type PolymorphicProps<T extends ElementType> = {
  as?: T;
  children?: ReactNode;
} & ComponentPropsWithoutRef<T>;

type SlideoutPanelTriggerProps<T extends ElementType = "button"> =
  PolymorphicProps<T>;
type SlideoutPanelBackButtonProps<T extends ElementType = "button"> =
  PolymorphicProps<T>;

const SlideoutPanelContext = createContext<
  SlideoutPanelContextValue | undefined
>(undefined);

const useSlideoutPanelContext = (): SlideoutPanelContextValue => {
  const context = useContext(SlideoutPanelContext);

  if (!context) {
    throw new Error(
      "useSlideoutPanelContext must be used within a SlideoutPanel component",
    );
  }

  return context;
};

/**
 * The root component for the SlideoutPanel. It manages the open state and provides
 * context to its children.
 *
 * It can be controlled via props or managed internally via the `SlideoutPanel.Trigger`
 * and `SlideoutPanel.BackButton` components.
 */
const SlideoutPanelRoot: React.FC<SlideoutPanelRootProps> = ({
  children,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    if (controlledOpen === undefined) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  const contextValue: SlideoutPanelContextValue = {
    isOpen,
    onOpen: () => handleOpenChange(true),
    onClose: () => handleOpenChange(false),
  };

  return (
    <SlideoutPanelContext.Provider value={contextValue}>
      {children}
    </SlideoutPanelContext.Provider>
  );
};

const SlideoutPanelTrigger = <T extends ElementType = "button">({
  as,
  children,
  onClick,
  ...props
}: SlideoutPanelTriggerProps<T>) => {
  const Component = as || "button";
  const { onOpen } = useSlideoutPanelContext();

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    onClick?.(e);
    if (!e.defaultPrevented) {
      onOpen();
    }
  };

  return (
    <Component onClick={handleClick} {...props}>
      {children}
    </Component>
  );
};

const SlideoutPanelBackButton = <T extends ElementType = "button">({
  as,
  children,
  onClick,
  ...props
}: SlideoutPanelBackButtonProps<T>) => {
  const Component = as || DefaultBackButton;
  const { onClose } = useSlideoutPanelContext();

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    onClick?.(e);
    if (!e.defaultPrevented) {
      onClose();
    }
  };

  return (
    <Component onClick={handleClick} {...props}>
      {children}
    </Component>
  );
};

const DefaultBackButton = (props: ComponentProps<"button">) => (
  <IconButton
    aria-label="Back"
    variant="outline-light"
    size="lg"
    icon={<ArrowLeftIcon />}
    {...props}
  />
);

// Content component
const SlideoutPanelContent: React.FC<SlideoutPanelContentProps> = ({
  children,
  name,
  className,
  onFinishClose,
}) => {
  const { isOpen, onClose } = useSlideoutPanelContext();
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = useRef<Element | null>(null);
  const [shouldRender, setShouldRender] = useState(false);

  // Track when we should render the component
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
    }
  }, [isOpen]);

  // Spring animations for overlay and panel
  const overlayAnimation = useSpring({
    opacity: isOpen ? 1 : 0,
    config: config.default,
    onRest: () => {
      // Only unmount after exit animation completes
      if (!isOpen) {
        setShouldRender(false);
        onFinishClose?.();
      }
    },
  });

  const panelAnimation = useSpring({
    transform: isOpen ? "translateX(0%)" : "translateX(100%)",
    config: config.default,
  });

  useSlideoutFocus({
    isOpen,
    panelRef,
    previouslyFocusedElementRef: previouslyFocusedElement,
  });

  useSlideoutKeyboardControls({
    isOpen,
    panelRef,
    onClose,
  });

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!shouldRender) return null;

  return (
    <Portal root={document.body}>
      <animated.div
        className={styles.overlay}
        style={overlayAnimation}
        onClick={handleOverlayClick}
        aria-hidden={!isOpen}
      />
      <AnimatedBox
        ref={panelRef}
        bg="primary"
        role="dialog"
        aria-modal="true"
        aria-label={name}
        tabIndex={-1}
        className={clsx(styles.panel, className)}
        style={panelAnimation}
      >
        {children}
      </AnimatedBox>
    </Portal>
  );
};

const AnimatedBox = animated(Box);

const SlideoutPanel = {
  Root: SlideoutPanelRoot,
  Trigger: SlideoutPanelTrigger,
  Content: SlideoutPanelContent,
  BackButton: SlideoutPanelBackButton,
};

export { SlideoutPanel, useSlideoutPanelContext };
